import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { adminApi, apiError } from "../api";
import { resolveImg } from "../../context/SiteContext";
import ImagePicker, { ImagePickerDialog } from "../components/ImagePicker";
import ListEditor from "../components/ListEditor";

const STATES = ["Meghalaya", "Assam", "Arunachal Pradesh"];

const EMPTY = {
  name: "", slug: "", state: "Meghalaya", hero: "", price: 0, blurb: "",
  best: "", duration: "", mapQuery: "", attractions: [], todo: [], nearby: [],
  gallery: [], itinerary: [], faqs: [], packageIds: [], seoTitle: "", metaDescription: "",
};

export default function DestinationsManager() {
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    adminApi.get("/admin/destinations").then(({ data }) => setItems(data)).catch((e) => toast.error(apiError(e)));
    adminApi.get("/admin/packages").then(({ data }) => setPackages(data)).catch(() => {});
  };
  useEffect(load, []);

  const filtered = items.filter((d) => d.name.toLowerCase().includes(q.toLowerCase()));

  const save = async () => {
    if (!editing.name) return toast.error("Name is required");
    setSaving(true);
    try {
      if (isNew) await adminApi.post("/admin/destinations", { ...editing, price: Number(editing.price) || 0 });
      else await adminApi.put(`/admin/destinations/${editing.slug}`, { ...editing, price: Number(editing.price) || 0 });
      toast.success("Saved — live on the website now");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slug, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await adminApi.delete(`/admin/destinations/${slug}`);
      toast.success("Destination deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const set = (k) => (v) => setEditing((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  return (
    <div data-testid="admin-destinations-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Destinations</h1>
          <p className="text-sm text-muted-foreground">{items.length} destinations · changes reflect instantly on the website</p>
        </div>
        <Button onClick={() => { setEditing({ ...EMPTY }); setIsNew(true); }} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="add-destination-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add destination
        </Button>
      </div>

      <div className="relative max-w-sm mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search destinations…" className="pl-9" data-testid="destinations-search" />
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((d) => (
          <div key={d.slug} className="rounded-xl border bg-white dark:bg-slate-900 overflow-hidden" data-testid={`dest-row-${d.slug}`}>
            <div className="h-32 bg-muted overflow-hidden">
              {d.hero && <img src={resolveImg(d.hero)} alt={d.name} className="h-full w-full object-cover" loading="lazy" />}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold">{d.name}</div>
                  <div className="text-xs text-muted-foreground">{d.state} · ₹{(d.price || 0).toLocaleString("en-IN")}</div>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditing({ ...EMPTY, ...d }); setIsNew(false); }} data-testid={`edit-dest-${d.slug}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => remove(d.slug, d.name)} data-testid={`delete-dest-${d.slug}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="destination-editor">
          <DialogHeader><DialogTitle>{isNew ? "Add destination" : `Edit — ${editing?.name}`}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid gap-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="grid gap-2"><Label>Title / Name</Label><Input value={editing.name} onChange={set("name")} data-testid="dest-field-name" /></div>
                <div className="grid gap-2"><Label>State</Label>
                  <Select value={editing.state} onValueChange={set("state")}>
                    <SelectTrigger data-testid="dest-field-state"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2"><Label>Starting price (₹/person)</Label><Input type="number" value={editing.price} onChange={set("price")} data-testid="dest-field-price" /></div>
                <div className="grid gap-2"><Label>Duration</Label><Input value={editing.duration} onChange={set("duration")} placeholder="e.g. 1 – 2 Days" data-testid="dest-field-duration" /></div>
                <div className="grid gap-2 sm:col-span-2"><Label>Best time to visit</Label><Input value={editing.best} onChange={set("best")} placeholder="e.g. October – May" data-testid="dest-field-best" /></div>
              </div>
              <div className="grid gap-2"><Label>Description</Label><Textarea rows={3} value={editing.blurb} onChange={set("blurb")} data-testid="dest-field-blurb" /></div>
              <ImagePicker label="Cover image" value={editing.hero} onChange={set("hero")} folder="destinations" />
              <div className="grid gap-2"><Label>Location (Google Maps query)</Label><Input value={editing.mapQuery} onChange={set("mapQuery")} placeholder="e.g. Shillong, Meghalaya, India" data-testid="dest-field-map" /></div>

              <ListEditor label="Highlights / Top attractions" items={editing.attractions} onChange={set("attractions")} />
              <ListEditor label="Things to do" items={editing.todo} onChange={set("todo")} />
              <ListEditor label="Nearby places (names)" items={editing.nearby} onChange={set("nearby")} />

              <div className="grid gap-2">
                <Label>Gallery images</Label>
                <div className="flex flex-wrap gap-3">
                  {(editing.gallery || []).map((g, i) => (
                    <div key={i} className="relative h-16 w-24 rounded-lg overflow-hidden border group">
                      <img src={resolveImg(g)} alt="" className="h-full w-full object-cover" />
                      <button onClick={() => set("gallery")(editing.gallery.filter((_, k) => k !== i))} className="absolute inset-0 bg-black/60 text-white text-xs hidden group-hover:block">Remove</button>
                    </div>
                  ))}
                  <GalleryAdd onAdd={(url) => set("gallery")([...(editing.gallery || []), url])} />
                </div>
              </div>

              <div className="grid gap-2">
                <Label>Suggested itinerary</Label>
                {(editing.itinerary || []).map((it, i) => (
                  <div key={i} className="grid grid-cols-[60px_1fr_auto] gap-2 items-start">
                    <Input type="number" value={it.day} onChange={(e) => set("itinerary")(editing.itinerary.map((x, k) => (k === i ? { ...x, day: Number(e.target.value) } : x)))} />
                    <div className="grid gap-2">
                      <Input value={it.title} placeholder="Day title" onChange={(e) => set("itinerary")(editing.itinerary.map((x, k) => (k === i ? { ...x, title: e.target.value } : x)))} />
                      <Textarea rows={2} value={it.desc} placeholder="Description" onChange={(e) => set("itinerary")(editing.itinerary.map((x, k) => (k === i ? { ...x, desc: e.target.value } : x)))} />
                    </div>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => set("itinerary")(editing.itinerary.filter((_, k) => k !== i))}>✕</Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" className="w-fit" onClick={() => set("itinerary")([...(editing.itinerary || []), { day: (editing.itinerary?.length || 0) + 1, title: "", desc: "" }])}>
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Add day
                </Button>
              </div>

              <div className="grid gap-2">
                <Label>FAQs</Label>
                {(editing.faqs || []).map((f, i) => (
                  <div key={i} className="grid grid-cols-[1fr_auto] gap-2 items-start">
                    <div className="grid gap-2">
                      <Input value={f.q} placeholder="Question" onChange={(e) => set("faqs")(editing.faqs.map((x, k) => (k === i ? { ...x, q: e.target.value } : x)))} />
                      <Textarea rows={2} value={f.a} placeholder="Answer" onChange={(e) => set("faqs")(editing.faqs.map((x, k) => (k === i ? { ...x, a: e.target.value } : x)))} />
                    </div>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => set("faqs")(editing.faqs.filter((_, k) => k !== i))}>✕</Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" className="w-fit" onClick={() => set("faqs")([...(editing.faqs || []), { q: "", a: "" }])}>
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Add FAQ
                </Button>
              </div>

              <div className="grid gap-2">
                <Label>Linked tour packages</Label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {packages.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={(editing.packageIds || []).includes(p.id)}
                        onCheckedChange={(v) => set("packageIds")(v ? [...(editing.packageIds || []), p.id] : editing.packageIds.filter((x) => x !== p.id))}
                      />
                      {p.title}
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="grid gap-2"><Label>SEO title</Label><Input value={editing.seoTitle || ""} onChange={set("seoTitle")} data-testid="dest-field-seo-title" /></div>
                <div className="grid gap-2"><Label>Meta description</Label><Input value={editing.metaDescription || ""} onChange={set("metaDescription")} data-testid="dest-field-meta" /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="save-destination-btn">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GalleryAdd({ onAdd }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} className="h-16 w-24 rounded-lg border-2 border-dashed grid place-items-center text-xs text-muted-foreground hover:border-emerald-400" data-testid="dest-gallery-add">
        + Add
      </button>
      <ImagePickerDialog open={open} onOpenChange={setOpen} defaultFolder="destinations" onSelect={(url) => { onAdd(url); setOpen(false); }} />
    </>
  );
}
